// Adaptador: adminEvents.ts → consume del motor contentMaster.ts
// Exporta las mismas interfaces y datos que esperan las páginas.

import {
  canonicalEvents,
  getPhotoUrl,
  type CanonicalEvent,
} from './contentMaster';

export type EventType = 'concierto' | 'meet_greet' | 'transmision_especial' | 'firma_autografos' | 'sesion_acustica' | 'lanzamiento';
export type EventStatus = 'borrador' | 'publicado' | 'finalizado' | 'cancelado';
export type EventModality = 'presencial' | 'online' | 'hibrido';
export type EventCategory = 'musica' | 'deportes' | 'comunidad' | 'cultural' | 'religioso' | 'familiar' | 'educativo';

export interface EventParticipant {
  id: number;
  name: string;
  avatar: string;
  confirmedAt: string;
}

export interface RadioEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  type: EventType;
  status: EventStatus;
  modality: EventModality;
  capacity: number;
  category: EventCategory;
  stationId: number;
  participants: number;
  interested: number;
  shares: number;
  followers: number;
  image: string;
  highlighted: boolean;
}

export const eventTypeLabels: Record<EventType, string> = {
  concierto: 'Concierto',
  meet_greet: 'Meet & Greet',
  transmision_especial: 'Transmisión especial',
  firma_autografos: 'Firma de autógrafos',
  sesion_acustica: 'Sesión acústica',
  lanzamiento: 'Lanzamiento',
};

export const eventTypeIcons: Record<EventType, string> = {
  concierto: 'ri-mic-2-line',
  meet_greet: 'ri-hand-heart-line',
  transmision_especial: 'ri-broadcast-line',
  firma_autografos: 'ri-pen-nib-line',
  sesion_acustica: 'ri-music-2-line',
  lanzamiento: 'ri-rocket-2-line',
};

export const eventTypeColors: Record<EventType, { bg: string; text: string; border: string }> = {
  concierto:           { bg: 'bg-rose-50',     text: 'text-rose-700',     border: 'border-rose-300' },
  meet_greet:          { bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-300' },
  transmision_especial: { bg: 'bg-emerald-50',  text: 'text-emerald-700',  border: 'border-emerald-300' },
  firma_autografos:    { bg: 'bg-sky-50',      text: 'text-sky-700',      border: 'border-sky-300' },
  sesion_acustica:     { bg: 'bg-violet-50',   text: 'text-violet-700',   border: 'border-violet-300' },
  lanzamiento:         { bg: 'bg-teal-50',     text: 'text-teal-700',     border: 'border-teal-300' },
};

export const eventStatusLabels: Record<EventStatus, string> = {
  borrador: 'Borrador',
  publicado: 'Publicado',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
};

export const eventStatusColors: Record<EventStatus, { dot: string; text: string; bg: string }> = {
  borrador:    { dot: 'bg-foreground-400', text: 'text-foreground-500', bg: 'bg-foreground-100' },
  publicado:   { dot: 'bg-emerald-500',    text: 'text-emerald-700',    bg: 'bg-emerald-50' },
  finalizado:  { dot: 'bg-foreground-400', text: 'text-foreground-500', bg: 'bg-foreground-100' },
  cancelado:   { dot: 'bg-red-500',        text: 'text-red-700',        bg: 'bg-red-50' },
};

export const eventModalityLabels: Record<EventModality, string> = {
  presencial: 'Físico',
  online: 'Online',
  hibrido: 'Híbrido',
};

export const eventModalityIcons: Record<EventModality, string> = {
  presencial: 'ri-map-pin-line',
  online: 'ri-global-line',
  hibrido: 'ri-smartphone-line',
};

export const eventModalityColors: Record<EventModality, { bg: string; text: string; border: string; dot: string; cardBg: string }> = {
  presencial: { bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-300',   dot: 'bg-amber-500',   cardBg: 'bg-amber-50/60' },
  online:     { bg: 'bg-emerald-50',  text: 'text-emerald-700',  border: 'border-emerald-300', dot: 'bg-emerald-500',  cardBg: 'bg-emerald-50/60' },
  hibrido:    { bg: 'bg-violet-50',   text: 'text-violet-700',   border: 'border-violet-300',  dot: 'bg-violet-500',  cardBg: 'bg-violet-50/60' },
};

export const eventCategoryLabels: Record<EventCategory, string> = {
  musica: 'Música',
  deportes: 'Deportes',
  comunidad: 'Comunidad',
  cultural: 'Cultural',
  religioso: 'Religioso',
  familiar: 'Familiar',
  educativo: 'Educativo',
};

export const eventCategoryIcons: Record<EventCategory, string> = {
  musica: 'ri-music-2-line',
  deportes: 'ri-football-line',
  comunidad: 'ri-group-line',
  cultural: 'ri-palette-line',
  religioso: 'ri-cross-line',
  familiar: 'ri-hearts-line',
  educativo: 'ri-book-open-line',
};

export const eventCategoryColors: Record<EventCategory, { bg: string; text: string; border: string }> = {
  musica:     { bg: 'bg-rose-50',     text: 'text-rose-700',     border: 'border-rose-300' },
  deportes:   { bg: 'bg-emerald-50',  text: 'text-emerald-700',  border: 'border-emerald-300' },
  comunidad:  { bg: 'bg-teal-50',     text: 'text-teal-700',     border: 'border-teal-300' },
  cultural:   { bg: 'bg-amber-50',    text: 'text-amber-700',    border: 'border-amber-300' },
  religioso:  { bg: 'bg-sky-50',      text: 'text-sky-700',      border: 'border-sky-300' },
  familiar:   { bg: 'bg-violet-50',   text: 'text-violet-700',   border: 'border-violet-300' },
  educativo:  { bg: 'bg-indigo-50',   text: 'text-indigo-700',   border: 'border-indigo-300' },
};

export const mockEventParticipants: EventParticipant[] = [
  { id: 1, name: 'Andrea Castillo', avatar: 'https://readdy.ai/api/search-image?query=Warm%20friendly%20Hispanic%20female%20portrait%2C%20genuine%20smile%2C%20casual%20modern%20style%2C%20soft%20natural%20lighting%2C%20clean%20background%2C%20lifestyle%20portrait&width=80&height=80&seq=eventp-1&orientation=squarish', confirmedAt: '2026-06-08T09:15:00' },
  { id: 2, name: 'Roberto Méndez', avatar: 'https://readdy.ai/api/search-image?query=Confident%20Hispanic%20male%20portrait%2C%20warm%20smile%2C%20casual%20streetwear%2C%20natural%20daylight%2C%20urban%20background%2C%20modern%20lifestyle%20photography&width=80&height=80&seq=eventp-2&orientation=squarish', confirmedAt: '2026-06-08T10:30:00' },
  { id: 3, name: 'María José Torres', avatar: 'https://readdy.ai/api/search-image?query=Elegant%20young%20Hispanic%20female%20portrait%2C%20professional%20warm%20smile%2C%20modern%20business%20casual%2C%20soft%20studio%20lighting%2C%20clean%20polished%20headshot&width=80&height=80&seq=eventp-3&orientation=squarish', confirmedAt: '2026-06-08T11:45:00' },
  { id: 4, name: 'Carlos Vega', avatar: 'https://readdy.ai/api/search-image?query=Charismatic%20Hispanic%20male%20portrait%2C%20friendly%20warm%20expression%2C%20modern%20casual%20attire%2C%20natural%20window%20light%2C%20relaxed%20lifestyle%20photography&width=80&height=80&seq=eventp-4&orientation=squarish', confirmedAt: '2026-06-09T08:00:00' },
  { id: 5, name: 'Sofía Ramírez', avatar: 'https://readdy.ai/api/search-image?query=Joyful%20young%20Hispanic%20female%20portrait%2C%20radiant%20smile%2C%20colorful%20blouse%2C%20bright%20natural%20lighting%2C%20soft%20urban%20background%2C%20happy%20lifestyle%20photography&width=80&height=80&seq=eventp-5&orientation=squarish', confirmedAt: '2026-06-09T14:20:00' },
  { id: 6, name: 'Diego Hernández', avatar: 'https://readdy.ai/api/search-image?query=Cool%20young%20Hispanic%20male%20portrait%2C%20confident%20friendly%20expression%2C%20modern%20streetwear%2C%20natural%20light%2C%20urban%20background%2C%20authentic%20lifestyle%20photography&width=80&height=80&seq=eventp-6&orientation=squarish', confirmedAt: '2026-06-10T07:30:00' },
  { id: 7, name: 'Valentina López', avatar: 'https://readdy.ai/api/search-image?query=Beautiful%20young%20Hispanic%20female%20portrait%2C%20warm%20genuine%20smile%2C%20casual%20chic%20outfit%2C%20golden%20hour%20light%2C%20soft%20urban%20background%2C%20lifestyle%20photography&width=80&height=80&seq=eventp-7&orientation=squarish', confirmedAt: '2026-06-10T16:00:00' },
  { id: 8, name: 'Mateo González', avatar: 'https://readdy.ai/api/search-image?query=Young%20Hispanic%20male%20portrait%20with%20warm%20smile%2C%20casual%20hoodie%20style%2C%20natural%20afternoon%20light%2C%20relaxed%20outdoor%20urban%20setting%2C%20genuine%20lifestyle%20photography&width=80&height=80&seq=eventp-8&orientation=squarish', confirmedAt: '2026-06-11T09:45:00' },
];

// ─── Mapeo de canonical a legacy ──────────────────────────

function toLegacyEventId(canonicalId: string): number {
  return parseInt(canonicalId.split('-')[1], 10);
}

function mapEventType(et: CanonicalEvent['eventType']): EventType {
  switch (et) {
    case 'concert': return 'concierto';
    case 'meet_greet': return 'meet_greet';
    case 'special_broadcast': return 'transmision_especial';
    case 'autograph': return 'firma_autografos';
    case 'acoustic_session': return 'sesion_acustica';
    case 'launch': return 'lanzamiento';
    default: return 'concierto';
  }
}

function mapEventStatus(s: CanonicalEvent['status']): EventStatus {
  switch (s) {
    case 'draft': return 'borrador';
    case 'published': return 'publicado';
    case 'ended': return 'finalizado';
    case 'cancelled': return 'cancelado';
    default: return 'borrador';
  }
}

function mapEventCategory(c: CanonicalEvent['category']): EventCategory {
  switch (c) {
    case 'music': return 'musica';
    case 'sports': return 'deportes';
    case 'community': return 'comunidad';
    case 'cultural': return 'cultural';
    case 'religious': return 'religioso';
    case 'family': return 'familiar';
    case 'educational': return 'educativo';
    default: return 'musica';
  }
}

function mapModality(m: CanonicalEvent['modality']): EventModality {
  return m as EventModality;
}

function mapEvent(e: CanonicalEvent): RadioEvent {
  return {
    id: toLegacyEventId(e.id),
    title: e.title,
    description: e.description,
    date: e.date,
    time: e.time,
    location: e.location,
    type: mapEventType(e.eventType),
    status: mapEventStatus(e.status),
    modality: mapModality(e.modality),
    capacity: e.capacity,
    category: mapEventCategory(e.category),
    stationId: e.stationId,
    participants: e.participants,
    interested: e.interested,
    shares: e.shares,
    followers: e.followers,
    image: getPhotoUrl(e.imagePhotoId),
    highlighted: e.highlighted,
  };
}

export const adminEvents: RadioEvent[] = canonicalEvents.map(mapEvent);

export function getStationEvents(stationId: number): RadioEvent[] {
  return adminEvents.filter((e) => e.stationId === stationId);
}

export function getActiveEvents(stationId: number): RadioEvent[] {
  return getStationEvents(stationId).filter((e) => e.status === 'publicado');
}

export function getUpcomingEvents(stationId: number): RadioEvent[] {
  return getStationEvents(stationId).filter((e) => e.status === 'publicado');
}

export function getEventParticipants(eventId: number): EventParticipant[] {
  const event = adminEvents.find((e) => e.id === eventId);
  if (!event) return [];
  return mockEventParticipants.slice(0, Math.min(8, Math.ceil(event.participants / 100) + 3));
}